import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from "react";
import { emitAnalyticsEvent } from "@/lib/analyticsClient";
import { trackIdentityKey } from "@/utils/trackIdentity";

export const PLAY_COUNT_MIN_SECONDS = 5;

export interface PlaylistTrack {
  title?: string;
  id?: string;
  name?: string;
  artist?: string;
  artwork?: string;
  audioUrl?: string;
  spotifyUrl?: string;
  albumName?: string;
}

export type PlaybackMode = "normal" | "repeat-track" | "repeat-queue";

interface PlaylistContextValue {
  setPlaybackMode: (mode: PlaybackMode) => void;
  replacePlaylist: (tracks: PlaylistTrack[], restart?: boolean) => void;
  playlist: PlaylistTrack[];
  currentTrack: PlaylistTrack | undefined;
  currentTrackIndex: number;
  isPlaying: boolean;
  isPanelOpen: boolean;
  isPlayerVisible: boolean;
  isPlayerModalOpen: boolean;
  albums: unknown;
  artistName: string | null;
  audioRef: React.RefObject<HTMLAudioElement | null>;
  analyserNode: AnalyserNode | null;
  addToPlaylist: (track: PlaylistTrack) => void;
  addManyToPlaylist: (tracks: PlaylistTrack[]) => number;
  enqueueTracks: (tracks: PlaylistTrack[]) => number;
  removeFromPlaylist: (index: number) => void;
  playTrack: (index: number) => void;
  togglePlayPause: () => void;
  playNext: () => void;
  playPrevious: () => void;
  seek: (time: number) => void;
  clearPlaylist: () => void;
  togglePanel: () => void;
  togglePlayerVisibility: () => void;
  togglePlayerModal: () => void;
  closePlayerModal: () => void;
  closeAllPanels: () => void;
  setAlbums: React.Dispatch<React.SetStateAction<unknown>>;
  setArtistName: React.Dispatch<React.SetStateAction<string | null>>;
}

interface PlaylistProgressValue {
  currentTime: number;
  duration: number;
}

const PlaylistContext = createContext<PlaylistContextValue | null>(null);
const PlaylistProgressContext = createContext<PlaylistProgressValue | null>(null);

export const usePlaylist = (): PlaylistContextValue => {
  const context = useContext(PlaylistContext);
  if (!context) {
    throw new Error("usePlaylist must be used within a PlaylistProvider");
  }
  return context;
};

export const usePlaylistProgress = (): PlaylistProgressValue => {
  const context = useContext(PlaylistProgressContext);
  if (!context) {
    throw new Error("usePlaylistProgress must be used within a PlaylistProvider");
  }
  return context;
};

interface PlaylistProviderProps {
  children: ReactNode;
}

export const isAutoplayBlocked = (error: unknown) => typeof error === "object" && error !== null && (error as { name?: unknown }).name === "NotAllowedError";

const isSameTrack = (candidate: PlaylistTrack, track: PlaylistTrack) => {
  if (candidate.audioUrl && candidate.audioUrl === track.audioUrl) return true;
  if (candidate.id && candidate.id === track.id) return true;
  const candidateKey = trackIdentityKey(candidate.name || candidate.title);
  const trackKey = trackIdentityKey(track.name || track.title);
  return Boolean(candidateKey) && candidateKey === trackKey && (candidate.artist || "").toLowerCase() === (track.artist || "").toLowerCase();
};

export const PlaylistProvider = ({ children }: PlaylistProviderProps) => {
  const [playlist, setPlaylist] = useState<PlaylistTrack[]>([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isPlayerVisible, setIsPlayerVisible] = useState(true);
  const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
  const [albums, setAlbums] = useState<unknown>(null);
  const [artistName, setArtistName] = useState<string | null>(null);
  const [analyserNode, setAnalyserNode] = useState<AnalyserNode | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<MediaElementAudioSourceNode | null>(null);
  const hasCountedPlayRef = useRef(false);

  const [playbackMode, setMode] = useState<PlaybackMode>("normal");
  const playbackModeRef = useRef<PlaybackMode>("normal");
  const failedUrlsRef = useRef(new Set<string>());
  const [playRequest, setPlayRequest] = useState(0);
  const playGenerationRef = useRef(0);

  const setPlaybackMode = useCallback((mode: PlaybackMode) => {
    playbackModeRef.current = mode;
    setMode(mode);
    if (audioRef.current) audioRef.current.loop = mode === "repeat-track";
    failedUrlsRef.current.clear();
  }, []);

  const currentTrack = playlist[currentTrackIndex];

  const playlistRef = useRef<PlaylistTrack[]>(playlist);
  playlistRef.current = playlist;
  const currentTrackIndexRef = useRef(currentTrackIndex);
  currentTrackIndexRef.current = currentTrackIndex;
  const currentTrackRef = useRef<PlaylistTrack | undefined>(currentTrack);
  currentTrackRef.current = currentTrack;
  const artistNameRef = useRef<string | null>(artistName);
  artistNameRef.current = artistName;
  const isPlayingRef = useRef(isPlaying);
  isPlayingRef.current = isPlaying;
  const isPanelOpenRef = useRef(isPanelOpen);
  isPanelOpenRef.current = isPanelOpen;
  const isPlayerModalOpenRef = useRef(isPlayerModalOpen);
  isPlayerModalOpenRef.current = isPlayerModalOpen;

  const playTrack = useCallback((index: number) => {
    if (index < 0 || index >= playlistRef.current.length) return;
    hasCountedPlayRef.current = false;
    currentTrackIndexRef.current = index;
    setCurrentTrackIndex(index);
    setIsPlaying(true);
    setCurrentTime(0);
    playGenerationRef.current += 1;
    setPlayRequest(playGenerationRef.current);
  }, []);

  // An arcade setlist owns its order. Preserve the audible track when more
  // previews arrive, even if its index moves. Restart only for a new run.
  const replacePlaylist = useCallback((tracks: PlaylistTrack[], restart = false) => {
    const next: PlaylistTrack[] = [];
    for (const track of tracks) {
      if (track.audioUrl && !next.some((item) => isSameTrack(item, track))) next.push(track);
    }
    const active = playlistRef.current[currentTrackIndexRef.current];
    const index = active ? next.findIndex((item) => isSameTrack(item, active)) : -1;
    playlistRef.current = next;
    setPlaylist(next);
    currentTrackIndexRef.current = Math.max(0, index);
    setCurrentTrackIndex(Math.max(0, index));
    if (restart || index < 0) {
      failedUrlsRef.current.clear();
      if (next.length) playTrack(0);
      else setIsPlaying(false);
    }
  }, [playTrack]);

  const addToPlaylist = useCallback(
    (track: PlaylistTrack) => {
      const existingIndex = playlistRef.current.findIndex((candidate) => isSameTrack(candidate, track));

      if (existingIndex !== -1) {
        playTrack(existingIndex);
        return;
      }

      const nextIndex = playlistRef.current.length;
      const nextPlaylist = [...playlistRef.current, track];
      playlistRef.current = nextPlaylist;
      setPlaylist(nextPlaylist);
      playTrack(nextIndex);
    },
    [playTrack]
  );

  const appendTracks = useCallback((tracks: PlaylistTrack[]) => {
    const playableTracks = tracks.filter((track) => track.audioUrl);
    if (playableTracks.length === 0) return { added: 0, firstIndex: -1 };

    const nextPlaylist = [...playlistRef.current];
    let firstIndex = -1;

    playableTracks.forEach((track) => {
      const existingIndex = nextPlaylist.findIndex((candidate) => isSameTrack(candidate, track));

      if (existingIndex !== -1) {
        if (firstIndex === -1) firstIndex = existingIndex;
        return;
      }

      if (firstIndex === -1) firstIndex = nextPlaylist.length;
      nextPlaylist.push(track);
    });

    if (firstIndex === -1) return { added: 0, firstIndex: -1 };

    const added = nextPlaylist.length - playlistRef.current.length;
    playlistRef.current = nextPlaylist;
    setPlaylist(nextPlaylist);
    return { added, firstIndex };
  }, []);

  const addManyToPlaylist = useCallback(
    (tracks: PlaylistTrack[]) => {
      const { added, firstIndex } = appendTracks(tracks);
      if (firstIndex === -1) return 0;
      playTrack(firstIndex);
      return added;
    },
    [appendTracks, playTrack]
  );

  const enqueueTracks = useCallback(
    (tracks: PlaylistTrack[]) => {
      const { added } = appendTracks(tracks);
      return added;
    },
    [appendTracks]
  );

  const removeFromPlaylist = useCallback((index: number) => {
    const nextPlaylist = playlistRef.current.filter((_, i) => i !== index);
    playlistRef.current = nextPlaylist;
    setPlaylist(nextPlaylist);

    const activeIndex = currentTrackIndexRef.current;
    if (index === activeIndex) {
      setIsPlaying(false);
      setCurrentTime(0);
    } else if (index < activeIndex) {
      currentTrackIndexRef.current = activeIndex - 1;
      setCurrentTrackIndex(activeIndex - 1);
    }
  }, []);

  const togglePlayPause = useCallback(() => {
    setIsPlaying((prev) => !prev);
  }, []);

  const playNext = useCallback(() => {
    const index = currentTrackIndexRef.current;
    if (index < playlistRef.current.length - 1) playTrack(index + 1);
  }, [playTrack]);

  const playPrevious = useCallback(() => {
    const index = currentTrackIndexRef.current;
    if (index > 0) playTrack(index - 1);
  }, [playTrack]);

  const seek = useCallback((time: number) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  }, []);

  const clearPlaylist = useCallback(() => {
    playlistRef.current = [];
    currentTrackIndexRef.current = 0;
    hasCountedPlayRef.current = false;
    setPlaylist([]);
    setCurrentTrackIndex(0);
    setIsPlaying(false);
    setCurrentTime(0);
  }, []);

  const togglePanel = useCallback(() => {
    setIsPanelOpen((prev) => !prev);
  }, []);

  const togglePlayerVisibility = useCallback(() => {
    setIsPlayerVisible((prev) => !prev);
  }, []);

  const togglePlayerModal = useCallback(() => {
    setIsPlayerModalOpen((prev) => !prev);
  }, []);

  const closePlayerModal = useCallback(() => {
    setIsPlayerModalOpen(false);
  }, []);

  const closeAllPanels = useCallback(() => {
    setIsPanelOpen(false);
    setIsPlayerModalOpen(false);
  }, []);

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.crossOrigin = "anonymous";
    }

    const audio = audioRef.current;

    const countPlay = () => {
      const track = currentTrackRef.current;
      const artist = artistNameRef.current;
      if (!track?.id || !artist) return;

      hasCountedPlayRef.current = true;
      emitAnalyticsEvent({
        eventName: "track_play",
        eventCategory: "player",
        artist,
        trackId: track.id,
        trackName: track.name || track.title || "Unknown",
        releaseName: track.albumName || track.title,
        metadata: { audioUrl: track.audioUrl, queueLength: playlistRef.current.length },
      });
      fetch("/.netlify/functions/TrackPlays", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "track",
          artist,
          trackId: track.id,
          trackName: track.name || track.title || "Unknown",
        }),
      }).catch((err) => console.error("Error tracking play:", err));
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);

      if (hasCountedPlayRef.current) return;
      if (audio.paused || audio.ended) return;
      if (audio.currentTime <= PLAY_COUNT_MIN_SECONDS) return;
      countPlay();
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleEnded = () => {
      const track = currentTrackRef.current;
      if (track?.id) {
        emitAnalyticsEvent({
          eventName: "track_complete",
          eventCategory: "player",
          artist: artistNameRef.current ?? undefined,
          trackId: track.id,
          trackName: track.name || track.title || "Unknown",
          releaseName: track.albumName || track.title,
          duration: Math.round((audio.duration || 0) * 1000),
        });
      }

      hasCountedPlayRef.current = false;
      const index = currentTrackIndexRef.current;
      if (index < playlistRef.current.length - 1) {
        playTrack(index + 1);
      } else if (playbackModeRef.current === "repeat-queue" && playlistRef.current.length) {
        playTrack(0);
      } else {
        setIsPlaying(false);
        setCurrentTime(0);
      }
    };

    const handleError = () => {
      if (playbackModeRef.current !== "repeat-queue") {
        setIsPlaying(false);
        return;
      }
      const failed = failedUrlsRef.current;
      if (audio.src) failed.add(audio.src);
      const tracks = playlistRef.current;
      for (let offset = 1; offset <= tracks.length; offset++) {
        const index = (currentTrackIndexRef.current + offset) % tracks.length;
        const url = tracks[index].audioUrl;
        if (url && !failed.has(new URL(url, window.location.href).href)) {
          playTrack(index);
          return;
        }
      }
      setIsPlaying(false);
    };

    audio.addEventListener("error", handleError);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("error", handleError);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [playTrack]);

  useEffect(() => {
    if (!audioRef.current) return;

    const setupAudioContext = () => {
      if (audioContextRef.current) return;

      try {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
        audioContextRef.current = audioContext;

        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.6;

        const source = audioContext.createMediaElementSource(audioRef.current!);
        source.connect(analyser);
        analyser.connect(audioContext.destination);

        sourceNodeRef.current = source;
        setAnalyserNode(analyser);
      } catch (error) {
        console.error("Error setting up audio context:", error);
      }
    };

    const handlePlay = () => {
      if (audioContextRef.current?.state === "suspended") {
        audioContextRef.current.resume();
      }
      if (!audioContextRef.current) {
        setupAudioContext();
      }
    };

    audioRef.current.addEventListener("play", handlePlay);

    return () => {
      audioRef.current?.removeEventListener("play", handlePlay);
    };
  }, []);

  const currentAudioUrl = currentTrack?.audioUrl;

  // Warm one upcoming media resource, never the full catalog. HTTP cache is
  // reused by the audio element and worker; leaving endless cancels the fetch.
  const nextUrl = playlist[(currentTrackIndex + 1) % Math.max(1, playlist.length)]?.audioUrl;
  useEffect(() => {
    if (playbackMode !== "repeat-queue" || !isPlaying || !nextUrl || nextUrl === currentAudioUrl) return;
    const controller = new AbortController();
    void fetch(nextUrl, { cache: "force-cache", signal: controller.signal }).then(response => {
      if (response.ok) return response.blob();
    }).catch(() => { /* The normal media error path handles unavailable previews. */ });
    return () => controller.abort();
  }, [nextUrl, currentAudioUrl, isPlaying, playbackMode]);


  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentAudioUrl) return;
    const url = new URL(currentAudioUrl, window.location.href).href;
    const sourceChanged = audio.src !== url;
    if (sourceChanged) audio.src = currentAudioUrl;
    audio.currentTime = 0;
    audio.loop = playbackModeRef.current === "repeat-track";
    hasCountedPlayRef.current = false;
    setCurrentTime(0);
    setDuration(!sourceChanged && Number.isFinite(audio.duration) ? audio.duration : 0);

    if (isPlayingRef.current) {
      const generation = playGenerationRef.current;
      audio.play().catch((error) => {
        if (generation !== playGenerationRef.current || audio.src !== url) return;
        if (playbackModeRef.current === "repeat-queue" && !isAutoplayBlocked(error)) return;
        if (!isAutoplayBlocked(error)) console.error("Error playing audio:", error);
        setIsPlaying(false);
      });
    }
  }, [currentAudioUrl, playRequest]);

  useEffect(() => {
    if (!audioRef.current) return;

    if (isPlaying && audioRef.current.paused) {
      const generation = playGenerationRef.current;
      audioRef.current.play().catch((error) => {
        if (generation !== playGenerationRef.current) return;
        if (playbackModeRef.current === "repeat-queue" && !isAutoplayBlocked(error)) return;
        if (!isAutoplayBlocked(error)) console.error("Error playing audio:", error);
        setIsPlaying(false);
      });
    } else if (!isPlaying && !audioRef.current.paused) {
      audioRef.current.pause();
    }
  }, [isPlaying]);

  useEffect(() => {
    const handleToggleMusicPlayer = () => {
      setIsPlayerModalOpen((prev) => !prev);
    };

    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (isPlayerModalOpenRef.current) {
        setIsPlayerModalOpen(false);
      } else if (isPanelOpenRef.current) {
        setIsPanelOpen(false);
      }
    };

    document.addEventListener("toggleMusicPlayer", handleToggleMusicPlayer);
    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("toggleMusicPlayer", handleToggleMusicPlayer);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, []);

  const value = useMemo<PlaylistContextValue>(
    () => ({
      setPlaybackMode,
      replacePlaylist,
      playlist,
      currentTrack,
      currentTrackIndex,
      isPlaying,
      isPanelOpen,
      isPlayerVisible,
      isPlayerModalOpen,
      albums,
      artistName,
      audioRef,
      analyserNode,
      addToPlaylist,
      addManyToPlaylist,
      enqueueTracks,
      removeFromPlaylist,
      playTrack,
      togglePlayPause,
      playNext,
      playPrevious,
      seek,
      clearPlaylist,
      togglePanel,
      togglePlayerVisibility,
      togglePlayerModal,
      closePlayerModal,
      closeAllPanels,
      setAlbums,
      setArtistName,
    }),
    [
      setPlaybackMode,
      replacePlaylist,
      playlist,
      currentTrack,
      currentTrackIndex,
      isPlaying,
      isPanelOpen,
      isPlayerVisible,
      isPlayerModalOpen,
      albums,
      artistName,
      analyserNode,
      addToPlaylist,
      addManyToPlaylist,
      enqueueTracks,
      removeFromPlaylist,
      playTrack,
      togglePlayPause,
      playNext,
      playPrevious,
      seek,
      clearPlaylist,
      togglePanel,
      togglePlayerVisibility,
      togglePlayerModal,
      closePlayerModal,
      closeAllPanels,
    ]
  );

  const progressValue = useMemo<PlaylistProgressValue>(() => ({ currentTime, duration }), [currentTime, duration]);

  return (
    <PlaylistContext.Provider value={value}>
      <PlaylistProgressContext.Provider value={progressValue}>{children}</PlaylistProgressContext.Provider>
    </PlaylistContext.Provider>
  );
};
